import { useCurrencyContext } from '@shared/utils/currency';
import { CurrencyFormat, CurrencyFormatOptions } from '@shared/utils/currency/types';
import { useCallback } from 'react';
import { useTranslation } from '@shared/core';
import { CurrencyCode, useGeoBasedLocale } from '@shared/hooks/useGeoLocale';
import { useIsFeatureEnabled } from '@shared/core/featureFlags';

export type MinorUnitsToPriceStringOptions = Readonly<
  {
    canShowFreeLabel?: boolean;
    freeLabel?: string;
    form?: CurrencyFormat;
    precision?: number;
    currency?: CurrencyCode;
  } & Omit<CurrencyFormatOptions, 'currency'>
>;

export const useMinorUnitsToPriceString = () => {
  const { currency: targetCurrency } = useGeoBasedLocale();
  const [isLocalCurrencyEnabled] = useIsFeatureEnabled('printLocalCurrency');
  const currency = useCurrencyContext();
  const { tq } = useTranslation('stationery');
  return useCallback(
    (price: number, { canShowFreeLabel, freeLabel, precision, currency: currencyCodeArg, ...rest }: MinorUnitsToPriceStringOptions) => {
      const currencyCode = currencyCodeArg ?? (isLocalCurrencyEnabled ? targetCurrency : currency.defaultCurrency);
      const priceInMajorUnits = price / 100;
      freeLabel = freeLabel ?? tq`FREE`;

      if (canShowFreeLabel && priceInMajorUnits === 0) {
        return freeLabel;
      }

      return currency.formatCurrency(priceInMajorUnits, {
        precision,
        currency: currencyCode,
        ...rest,
      });
    },
    [currency, tq, targetCurrency, isLocalCurrencyEnabled],
  );
};
